import gql from 'graphql-tag';

export const actionTypes = {
  login: {
    request: '@auth/LOGIN_REQUEST',
    success: '@auth/LOGIN_SUCCESS',
    error: '@auth/LOGIN_ERROR',
  },
  forgot: {
    request: '@auth/FORGOT_REQUEST',
    success: '@auth/FORGOT_SUCCESS',
    error: '@auth/FORGOT_ERROR',
    goBack: '@auth/FORGOT_GO_BACK',
  },
  reset: {
    request: '@auth/RESET_REQUEST',
    success: '@auth/RESET_SUCCESS',
    error: '@auth/RESET_ERROR',
  },
  renew: {
    request: '@auth/RENEW_REQUEST',
    success: '@auth/RENEW_SUCCESS',
    error: '@auth/RENEW_ERROR',
  },
  redirectUri: '@auth/SET_REDIRECT_URI',
  resetFlags: '@auth/RESET_FLAGS',
};

export const userFragment = gql`
  fragment userParts on WhoIm {
    personId
    name
    email
    phone
  }
`;

export const authFragment = gql`
  fragment authParts on Auth {
    idToken
    accessToken
    refreshToken
    passwordExpired
    mfaRequired
    enableMfaConfiguration
    session
    user {
      ...userParts
    }
  }
  ${userFragment}
`;

export const signInV2Mutation = gql`
  mutation signInV2($credentials: CredentialsInput!) {
    signInV2(credentials: $credentials) {
      ...authParts
      mfaEnrollmentSession
      workstationAuthorization {
        status
        partners {
          partnerContextId
          name
        }
        condominiums {
          id
          name
        }
      }
    }
  }
  ${authFragment}
`;

export const signInChallengeMutation = gql`
  mutation signInChallenge($username: String!) {
    signInChallenge(username: $username) {
      nonce
    }
  }
`;

// Repassa a mesma assinatura de nonce emitida para o signIn: quem tem TOTP
// cadastrado e está sujeito ao portão de workstation cai direto no desafio
// MFA antes do portão ser avaliado, então o segundo passo precisa levar a
// prova de posse junto para o BE avaliar o portão aqui também. Campos
// opcionais — usuários fora do piloto seguem enviando só session/code/username.
export const respondMfaMutation = gql`
  mutation respondMfa(
    $session: String!
    $code: String!
    $username: String!
    $publicKeyThumbprint: String
    $signature: String
  ) {
    respondMfa(
      session: $session
      code: $code
      username: $username
      publicKeyThumbprint: $publicKeyThumbprint
      signature: $signature
    ) {
      ...authParts
      workstationAuthorization {
        status
        partners {
          partnerContextId
          name
        }
        condominiums {
          id
          name
        }
      }
    }
  }
  ${authFragment}
`;

export const associateTokenMutation = gql`
  mutation associateToken {
    associateToken {
      secret
      otpAuthUri
    }
  }
`;

export const verifyTokenMutation = gql`
  mutation verifyToken($code: String!) {
    verifyToken(code: $code)
  }
`;

// Cadastro obrigatório de MFA: a credencial é a enrollmentSession, não o token.
export const associateMfaEnrollmentMutation = gql`
  mutation associateMfaEnrollment($enrollmentSession: String!) {
    associateMfaEnrollment(enrollmentSession: $enrollmentSession) {
      secret
      otpAuthUri
    }
  }
`;

// `user` só resolve aqui porque a resposta do verify tem accessToken
// (Auth.user consulta whoAmI com ele); no signIn desse fluxo vem null.
export const verifyMfaEnrollmentMutation = gql`
  mutation verifyMfaEnrollment($enrollmentSession: String!, $code: String!) {
    verifyMfaEnrollment(enrollmentSession: $enrollmentSession, code: $code) {
      idToken
      accessToken
      refreshToken
      user {
        ...userParts
      }
    }
  }
  ${userFragment}
`;

export const registerWorkstationMutation = gql`
  mutation registerWorkstation($input: WorkstationRegisterInput!) {
    registerWorkstation(input: $input) {
      status
    }
  }
`;

export const whoAmI = gql`
  query whoAmI {
    whoAmI {
      ...userParts
    }
  }
  ${userFragment}
`;

// Consulta anônima de status usada pelo poll da tela de espera de aprovação.
// Chaveada só pelo thumbprint — sem e-mail na identidade.
export const getWorkstationStatusQuery = gql`
  query getWorkstationStatus($publicKeyThumbprint: String!) {
    getWorkstationStatus(publicKeyThumbprint: $publicKeyThumbprint) {
      status
    }
  }
`;

export const forgotMutation = gql`
  mutation forgotPassword($username: String!) {
    forgot(username: $username)
  }
`;

export const resetMutation = gql`
  mutation resetPassword($resetPassword: ResetPasswordInput!) {
    reset(resetPassword: $resetPassword)
  }
`;

export const renewMutation = gql`
  mutation renewPassword($renewPassword: RenewPasswordInput!) {
    renew(renewPassword: $renewPassword) {
      renewed
      mfaCodeRequired
    }
  }
`;

export const setRedirectUri = uri => ({
  type: actionTypes.redirectUri,
  payload: uri,
});

export const resetFlags = () => ({ type: actionTypes.resetFlags });
